import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

import { PaymentMethodIcon } from "./PaymentMethodIcon";

export type PaymentMethodBarItem = {
  id: string;
  label: string;
  detail?: string | null;
  disabled?: boolean;
};

export function PaymentMethodBar({
  current,
  options,
  onSelect,
  ariaLabel = "Payment Method",
}: {
  current: PaymentMethodBarItem | null;
  options: readonly PaymentMethodBarItem[];
  onSelect: (methodId: string) => void;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const menuOptions = options.filter((option) => option.id !== current?.id);
  return (
    <div className="cashier-methodbar">
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        className="cashier-methodbar__bar"
        data-disabled={current?.disabled || undefined}
        disabled={!current}
        onClick={() => setOpen((value) => !value)}
        type="button"
      >
        <span className="cashier-methodbar__current">
          {current ? <PaymentMethodIcon methodId={current.id} /> : null}
          <span className="cashier-methodbar__label">
            {current?.label ?? "Payment methods unavailable"}
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className="cashier-methodbar__chevron"
          data-open={open || undefined}
          size={15}
        />
      </button>

      {open ? (
        <>
          <button
            aria-label="Close payment method menu"
            className="cashier-methodbar__backdrop"
            onClick={() => setOpen(false)}
            tabIndex={-1}
            type="button"
          />
          <div
            aria-label={`${ariaLabel} options`}
            className="cashier-methodbar__menu"
            role="listbox"
          >
            {menuOptions.map((option) => (
              <button
                aria-disabled={option.disabled || undefined}
                aria-selected={false}
                className="cashier-methodbar__option"
                disabled={option.disabled}
                key={option.id}
                onClick={() => {
                  setOpen(false);
                  onSelect(option.id);
                }}
                role="option"
                type="button"
              >
                <span className="cashier-methodbar__current">
                  <PaymentMethodIcon methodId={option.id} />
                  <span className="cashier-methodbar__option-copy">
                    <strong>{option.label}</strong>
                    {option.detail ? <small>{option.detail}</small> : null}
                  </span>
                </span>
                <ChevronRight aria-hidden size={15} />
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
